import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, RefreshCw, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";

/* Built-in image captcha — the bot check used while reCAPTCHA Enterprise isn't
   provisioned. GET /auth/captcha returns a signed token plus an SVG data URI
   that encodes the answer; the form posts the token back with whatever the user
   typed (see app/services/captcha.py).

   When Enterprise IS configured the endpoint replies {required:false} and this
   widget renders nothing, so pages don't need to know which mode is active. */

export interface CaptchaState {
  /** False when Enterprise is configured (or the endpoint is unreachable). */
  required: boolean;
  token: string;
  answer: string;
  image: string;
  loading: boolean;
  setAnswer: (value: string) => void;
  /** Fetch a fresh challenge — call after any failed submit: tokens are single-use. */
  refresh: () => void;
  /** Fields to merge into an auth request body. */
  payload: () => { captcha_token?: string; captcha_answer?: string };
  /** True when the widget is showing but unanswered — for disabling submit. */
  incomplete: boolean;
  /** Set when the challenge couldn't be loaded, so the widget can say so. */
  error: boolean;
}

export function useCaptcha(): CaptchaState {
  const [required, setRequired] = useState(false);
  const [token, setToken] = useState("");
  const [image, setImage] = useState("");
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const refresh = useCallback(() => {
    setLoading(true);
    setError(false);
    setAnswer("");
    api.get<{ required: boolean; token?: string; image?: string }>("/auth/captcha")
      .then((data) => {
        setRequired(Boolean(data.required));
        setToken(data.token || "");
        setImage(data.image || "");
      })
      .catch(() => {
        // Surface the failure instead of hiding: the backend still rejects a
        // submit without a captcha, so a silently-hidden widget would leave the
        // form permanently unusable with no visible reason.
        setError(true);
        setToken("");
        setImage("");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return {
    required,
    token,
    answer,
    image,
    loading,
    error,
    setAnswer,
    refresh,
    payload: () => (required ? { captcha_token: token, captcha_answer: answer } : {}),
    incomplete: required && answer.trim().length === 0,
  };
}

export function CaptchaField({ captcha, className = "" }: { captcha: CaptchaState; className?: string }) {
  if (captcha.error) {
    return (
      <div className={`flex items-center gap-2 rounded-xl border border-amber-500/25 bg-amber-500/10 px-3 py-2.5 text-xs text-foreground/90 ${className}`}>
        <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
        <span className="flex-1">Couldn't load the security check.</span>
        <button type="button" onClick={captcha.refresh} className="font-semibold text-primary underline-offset-2 hover:underline">
          Retry
        </button>
      </div>
    );
  }
  if (!captcha.required) return null;

  const complete = captcha.answer.length === 5;

  return (
    <div className={`space-y-1.5 ${className}`}>
      <div className="flex items-center justify-between">
        <label htmlFor="captcha-answer" className="flex items-center gap-1.5 text-sm font-medium text-foreground">
          <ShieldCheck className="h-3.5 w-3.5 text-primary" />
          Security check
        </label>
        <button
          type="button"
          onClick={captcha.refresh}
          disabled={captcha.loading}
          className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-primary disabled:opacity-50"
        >
          <RefreshCw className={`h-3 w-3 ${captcha.loading ? "animate-spin" : ""}`} />
          New image
        </button>
      </div>

      {/* Image and answer field read as one control: the image is the top half
          of a single rounded panel, the input the bottom half. */}
      <div className="overflow-hidden rounded-xl border border-border focus-within:border-primary/50 focus-within:ring-2 focus-within:ring-primary/20">
        <div className="border-b border-border bg-[#12100b] p-2.5">
          {/* Width-driven with h-auto: the SVG's 316×110 viewBox sets the height,
              so the panel never letterboxes or stretches the glyphs. */}
          {captcha.image ? (
            <img
              src={captcha.image}
              alt="Five digits to type below"
              className="mx-auto block h-auto w-full max-w-[280px] select-none"
              draggable={false}
            />
          ) : (
            <div className="mx-auto aspect-[316/110] w-full max-w-[280px] animate-pulse rounded-lg bg-white/5" />
          )}
        </div>
        <div className="relative">
          <input
            id="captcha-answer"
            value={captcha.answer}
            onChange={(e) => captcha.setAnswer(e.target.value.replace(/\D/g, "").slice(0, 5))}
            inputMode="numeric"
            autoComplete="off"
            aria-label="Type the five digits shown above"
            placeholder="Type the digits"
            className="h-11 w-full bg-transparent px-3 pr-10 text-center text-base font-semibold tracking-[0.5em] text-foreground placeholder:text-sm placeholder:font-normal placeholder:tracking-normal placeholder:text-muted-foreground/70 focus:outline-none"
          />
          {complete && (
            <CheckCircle2 className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-emerald-500" />
          )}
        </div>
      </div>
    </div>
  );
}
